import { useState, useRef, useCallback, useEffect } from "react";
import { tokenManager } from "../utils/utility";
import { getSpeechWsUrl } from "../store/apiActions";

const SAMPLE_RATE = 24000;

// Strip common Markdown syntax so it is never sent to the TTS WebSocket
// (the backend also cleans text before speaking, but doing it here too
// avoids relying solely on that safety net for the voice overlay/listen flows).
const stripMarkdownForVoice = (text) => {
  if (!text) return "";
  let cleaned = text;

  // Code fences and inline code
  cleaned = cleaned.replace(/```[\s\S]*?```/g, '');
  cleaned = cleaned.replace(/`([^`]*)`/g, '$1');

  // Images and links: keep the visible label, drop the markup
  cleaned = cleaned.replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1');
  cleaned = cleaned.replace(/\[([^\]]*)\]\([^)]*\)/g, '$1');

  // Headings (#, ##, ...)
  cleaned = cleaned.replace(/^[ \t]*#{1,6}[ \t]*/gm, '');

  // Bold/italic/strikethrough markers
  cleaned = cleaned.replace(/(\*\*\*|___)(.*?)\1/g, '$2');
  cleaned = cleaned.replace(/(\*\*|__)(.*?)\1/g, '$2');
  cleaned = cleaned.replace(/(\*|_)(.*?)\1/g, '$2');
  cleaned = cleaned.replace(/~~(.*?)~~/g, '$1');

  // Blockquotes and horizontal rules
  cleaned = cleaned.replace(/^[ \t]*>[ \t]?/gm, '');
  cleaned = cleaned.replace(/^[ \t]*([-*_][ \t]*){3,}$/gm, '');

  // Bulleted / numbered list markers at the start of a line
  cleaned = cleaned.replace(/^[ \t]*[-*+][ \t]+/gm, '');
  cleaned = cleaned.replace(/^[ \t]*\d+[.)][ \t]+/gm, '');

  // Markdown table rows/separators
  cleaned = cleaned.replace(/^[ \t]*\|?[-: \t|]{3,}\|?[ \t\r]*$/gm, '');
  cleaned = cleaned.replace(/\|/g, ' ');

  // Emojis
  cleaned = cleaned.replace(/[\p{Extended_Pictographic}\u{1F3FB}-\u{1F3FF}\u{FE0F}\u{200D}]/gu, '');

  return cleaned;
};

const getCleanedContent = (rawContent) => {
  if (!rawContent || rawContent === "Loading...") return "";
  let cleaned = rawContent;
  // Strip out local transcript block at the end if it exists.
  cleaned = cleaned.replace(/\[Transcript\][\s\S]*/gi, '');
  cleaned = stripMarkdownForVoice(cleaned);
  return cleaned;
};

/**
 * useStreamingVOP — Generic hook for WebSocket-based Streaming Text-to-Speech (Voice Output Player)
 *
 * Connects to a backend streaming TTS WebSocket endpoint, streams text deltas, and plays the returned PCM audio.
 */
const useStreamingVOP = ({
  content,
  isPlaceholder,
  enableVoiceOutput,
  autoPlayVoice = true,
  isAssistant,
  onVoiceOutputEnd,
  /** Called once when the first audio chunk starts playing (use to stop mic input). */
  onVoiceOutputStart,
  isStreamingVop = false,
  isAzureVop = false,
  wsUrl,
} = {}) => {
  const isVopActive = isStreamingVop || isAzureVop;
  const [isVoicePlaying, setIsVoicePlaying] = useState(false);
  const [isVoiceUnavailable, setIsVoiceUnavailable] = useState(false);

  const ttsWsRef = useRef(null);
  const audioCtxRef = useRef(null);
  const nextPlayTimeRef = useRef(0);

  const spokenIndexRef = useRef(0);
  const micTriggeredRef = useRef(false);
  const autoPlayEnabledRef = useRef(autoPlayVoice);
  const wasPlaceholderRef = useRef(isPlaceholder);
  const audioStartedRef = useRef(false);
  const instanceIdRef = useRef(Math.random().toString(36).substring(7));
  const voiceOutputStartFiredRef = useRef(false);

  const onVoiceOutputEndRef = useRef(onVoiceOutputEnd);
  const onVoiceOutputStartRef = useRef(onVoiceOutputStart);
  /** Set to true when the user explicitly says 'stop' — prevents auto-reconnect. */
  const stoppedByUserRef = useRef(false);
  const hasResetForThisMessageRef = useRef(false);
  /** Track previous autoPlayVoice so we can detect false→true transitions. */
  const prevAutoPlayRef = useRef(autoPlayVoice);

  useEffect(() => {
    onVoiceOutputEndRef.current = onVoiceOutputEnd;
  }, [onVoiceOutputEnd]);

  useEffect(() => {
    onVoiceOutputStartRef.current = onVoiceOutputStart;
  }, [onVoiceOutputStart]);

  useEffect(() => {
    autoPlayEnabledRef.current = autoPlayVoice;
    // When autoPlayVoice transitions false → true, a new voice-triggered
    // message is being sent. Reset all per-message refs so VOP starts fresh.
    if (autoPlayVoice && !prevAutoPlayRef.current) {
      setIsVoiceUnavailable(false);
      stoppedByUserRef.current = false;
      spokenIndexRef.current = 0;
      micTriggeredRef.current = false;
      voiceOutputStartFiredRef.current = false;
      hasResetForThisMessageRef.current = false;
      audioStartedRef.current = false;
    }
    prevAutoPlayRef.current = autoPlayVoice;
  }, [autoPlayVoice]);

  const ensureAudioCtx = useCallback(() => {
    if (!audioCtxRef.current || audioCtxRef.current.state === "closed") {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      audioCtxRef.current = new AudioContext({ sampleRate: SAMPLE_RATE });
      nextPlayTimeRef.current = audioCtxRef.current.currentTime;
    }
    if (audioCtxRef.current.state === "suspended") {
      audioCtxRef.current.resume();
    }
  }, []);

  const stop = useCallback((byUser = false) => {
    if (byUser) stoppedByUserRef.current = true;
    if (ttsWsRef.current && ttsWsRef.current.readyState === WebSocket.OPEN) {
      ttsWsRef.current.close();
    }
    ttsWsRef.current = null;

    if (audioCtxRef.current) {
      try { audioCtxRef.current.close(); } catch (_) { /* ignore */ }
      audioCtxRef.current = null;
      nextPlayTimeRef.current = 0;
    }

    voiceOutputStartFiredRef.current = false;
    setIsVoicePlaying(false);
    window.isStreamingVopPlaying = false;
    window.isAzureVopPlaying = false;
    if (typeof window !== "undefined") {
      window.currentSpeakingText = "";
    }
  }, []);

  const playPcm16 = useCallback((arrayBuffer) => {
    audioStartedRef.current = true;
    setIsVoiceUnavailable(false);
    ensureAudioCtx();
    const ctx = audioCtxRef.current;
    const raw = new Int16Array(arrayBuffer);
    const f32 = new Float32Array(raw.length);
    for (let i = 0; i < raw.length; i++) {
      f32[i] = raw[i] / (raw[i] < 0 ? 0x8000 : 0x7fff);
    }

    const buf = ctx.createBuffer(1, f32.length, SAMPLE_RATE);
    buf.copyToChannel(f32, 0);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.connect(ctx.destination);

    const start = Math.max(ctx.currentTime, nextPlayTimeRef.current);
    src.start(start);
    nextPlayTimeRef.current = start + buf.duration;

    setIsVoicePlaying(true);
    window.isStreamingVopPlaying = true;
    window.isAzureVopPlaying = true;
    if (typeof window !== "undefined") {
      window.currentSpeakingText = content || "";
    }

    // Fire onVoiceOutputStart once when the very first audio chunk plays
    if (!voiceOutputStartFiredRef.current && onVoiceOutputStartRef.current && autoPlayEnabledRef.current) {
      voiceOutputStartFiredRef.current = true;
      onVoiceOutputStartRef.current();
    }

    src.onended = () => {
      if (ctx.currentTime >= nextPlayTimeRef.current - 0.1) {
        setIsVoicePlaying(false);
        window.isStreamingVopPlaying = false;
        window.isAzureVopPlaying = false;
        if (typeof window !== "undefined") {
          window.currentSpeakingText = "";
        }
        // Trigger mic when done playing, if text stream is already complete
        if (!stoppedByUserRef.current && !wasPlaceholderRef.current && !micTriggeredRef.current && onVoiceOutputEndRef.current && autoPlayEnabledRef.current) {
          micTriggeredRef.current = true;
          const callback = onVoiceOutputEndRef.current;
          callback();
        }
      }
    };
  }, [ensureAudioCtx, content]);

  const connect = useCallback(() => {
    // Stop other playing instances
    window.dispatchEvent(new CustomEvent("streamingVopStop", { detail: { id: instanceIdRef.current } }));
    window.dispatchEvent(new CustomEvent("azureVopStop", { detail: { id: instanceIdRef.current } }));

    if (ttsWsRef.current && (ttsWsRef.current.readyState === WebSocket.OPEN || ttsWsRef.current.readyState === WebSocket.CONNECTING)) {
      return;
    }

    audioStartedRef.current = false;
    const token = tokenManager?.getToken("token") || "";
    const resolvedUrl = wsUrl || getSpeechWsUrl("tts");

    let ws;
    try {
      ws = new WebSocket(resolvedUrl);
    } catch (err) {
      console.error("Failed to open TTS WebSocket", err);
      setIsVoiceUnavailable(true);
      return;
    }

    ws.binaryType = "blob";

    ws.onopen = () => {
      if (token) {
        ws.send(JSON.stringify({ token }));
      }

      // If we already have pending text that was missed
      const cleanedContent = getCleanedContent(content);
      if (cleanedContent && spokenIndexRef.current < cleanedContent.length) {
        const newText = cleanedContent.substring(spokenIndexRef.current);
        ws.send(newText);
        spokenIndexRef.current = cleanedContent.length;
      }

      if (!isPlaceholder) {
        ws.send("");
      }
    };

    ws.onmessage = async (event) => {
      if (typeof event.data === "string") {
        try {
          const m = JSON.parse(event.data);
          console.error("TTS error from server:", m.detail || event.data);
        } catch {
          console.warn("TTS message:", event.data);
        }
        setIsVoiceUnavailable(true);
        return;
      }
      playPcm16(await event.data.arrayBuffer());
    };

    ws.onclose = () => {
      ttsWsRef.current = null;
      if (!audioStartedRef.current && !stoppedByUserRef.current) {
        setIsVoiceUnavailable(true);
      }
    };

    ws.onerror = (e) => {
      ttsWsRef.current = null;
      setIsVoiceUnavailable(true);
      console.error("TTS WebSocket error", e);
    };

    ttsWsRef.current = ws;
  }, [wsUrl, content, playPcm16, isPlaceholder]);

  const sendTextDelta = useCallback((delta) => {
    if (typeof delta !== "string" || !delta) return false;
    if (ttsWsRef.current && ttsWsRef.current.readyState === WebSocket.OPEN) {
      ttsWsRef.current.send(delta);
      return true;
    }
    return false;
  }, []);

  const finishTextStream = useCallback(() => {
    if (ttsWsRef.current && ttsWsRef.current.readyState === WebSocket.OPEN) {
      ttsWsRef.current.send("");
    }
  }, []);

  // Main streaming loop
  useEffect(() => {
    if (isPlaceholder && !hasResetForThisMessageRef.current) {
      setIsVoiceUnavailable(false);
      stoppedByUserRef.current = false;
      spokenIndexRef.current = 0;
      micTriggeredRef.current = false;
      voiceOutputStartFiredRef.current = false;
      audioStartedRef.current = false;
      hasResetForThisMessageRef.current = true;
    }

    if (!isPlaceholder) {
      hasResetForThisMessageRef.current = false;
    }

    if (!isVopActive || !autoPlayEnabledRef.current || !enableVoiceOutput || !isAssistant || !content || content === "Loading...") {
      return;
    }

    // Don't reconnect if user explicitly stopped playback
    if (stoppedByUserRef.current) return;

    connect();

    const cleanedContent = getCleanedContent(content);
    if (spokenIndexRef.current < cleanedContent.length) {
      const newText = cleanedContent.substring(spokenIndexRef.current);
      if (newText.length > 0) {
        const sent = sendTextDelta(newText);
        if (sent) {
          spokenIndexRef.current = cleanedContent.length;
        }
      }
    }

    if (wasPlaceholderRef.current && !isPlaceholder) {
      finishTextStream();
    }
    wasPlaceholderRef.current = isPlaceholder;

  }, [content, isPlaceholder, enableVoiceOutput, isAssistant, isVopActive, connect, sendTextDelta, finishTextStream]);

  // Handle auto-mic trigger fallback
  useEffect(() => {
    if (!stoppedByUserRef.current && !isPlaceholder && !isVoicePlaying && autoPlayEnabledRef.current && onVoiceOutputEndRef.current && !micTriggeredRef.current) {
      if (content && content !== "Loading...") {
        const timer = setInterval(() => {
          if (stoppedByUserRef.current || isVoicePlaying || micTriggeredRef.current) {
            clearInterval(timer);
            return;
          }
          if (!audioStartedRef.current && ttsWsRef.current != null) {
            return;
          }
          micTriggeredRef.current = true;
          const callback = onVoiceOutputEndRef.current;
          callback();
          clearInterval(timer);
        }, 200);
        return () => clearInterval(timer);
      }
    }
  }, [isPlaceholder, isVoicePlaying, content]);

  const handleVoiceToggle = useCallback(() => {
    if (isVoicePlaying) {
      stop();
      autoPlayEnabledRef.current = false;
    } else {
      setIsVoiceUnavailable(false);
      micTriggeredRef.current = true;
      autoPlayEnabledRef.current = true;

      spokenIndexRef.current = 0;
      connect();
      const cleanedContent = getCleanedContent(content);
      if (cleanedContent && ttsWsRef.current?.readyState === WebSocket.OPEN) {
        ttsWsRef.current.send(cleanedContent);
        ttsWsRef.current.send("");
        spokenIndexRef.current = cleanedContent.length;
      }
    }
  }, [isVoicePlaying, stop, connect, content]);

  useEffect(() => {
    return () => { if (enableVoiceOutput && isVopActive) stop(); };
  }, [enableVoiceOutput, isVopActive, stop]);

  // Listen for global stop events to ensure only one VOP plays at a time
  useEffect(() => {
    const handleGlobalStop = (e) => {
      if (!e.detail || e.detail.forceAll || e.detail.id !== instanceIdRef.current) {
        stop(e.detail?.forceAll ?? !e.detail);
      }
    };
    window.addEventListener("streamingVopStop", handleGlobalStop);
    window.addEventListener("azureVopStop", handleGlobalStop);
    return () => {
      window.removeEventListener("streamingVopStop", handleGlobalStop);
      window.removeEventListener("azureVopStop", handleGlobalStop);
    };
  }, [stop]);

  return {
    isVoicePlaying,
    isVoiceUnavailable,
    handleVoiceToggle
  };
};

export default useStreamingVOP;
